import { api, apiDownloadRequest } from './client';
import { stringifyParams } from '@/lib/utils';
import type { ApiResponse } from '@/types/api';
import type {
  EmployeeListParameters,
  EmployeeListResponse,
  EmployeeModResponse,
  EmployeeRegister,
  EmployeeIdsRequest,
  EmployeeIdsDelResponse,
  EmployeeUpdate,
  EmployeeExcelUploadParameters,
  EmployeeExcelUploadResponse,
  PlatformUserId,
  GroupNoChangeParameters,
  EmployeeGroupNoChangeParameters,
  EmployeeGroupNoChangeResponse,
  EmployeeListGroupNoChangeResponse,
  EmployeeListGroupNoChangeParameters,
  EmployeeTypeList,
  EmployeeType,
  EmployeeTypeBatch
} from '@/types/employee';
import type { ExcelUpsertTask } from '@/types/task';

/**
 * 임직원 리스트
 * @param params 
 * @returns 
 */
export async function getEmployeeList(params: EmployeeListParameters): Promise<ApiResponse<EmployeeListResponse>> {
  const stringParams = stringifyParams(params);
  return api.get<EmployeeListResponse>(`/employee`, { params: stringParams });
}

/**
 * 임직원 삭제 리스트
 * @param params 
 * @returns 
 */
export async function getEmployeeDeletedList(params: EmployeeListParameters): Promise<ApiResponse<EmployeeListResponse>> {
  const stringParams = stringifyParams(params);
  return api.get<EmployeeListResponse>(`/employee/deleted`, { params: stringParams });
}

/**
 * 임직원 등록
 * @param data 
 * @returns 
 */
export async function createEmployee(data: EmployeeRegister): Promise<ApiResponse<EmployeeModResponse>> {
  return api.post<EmployeeModResponse>('/employee', { body: data });
}

/**
 * 임직원 삭제
 * @param data EmployeeIdsRequest.employeeIds ,로 구분하여 여러개 전송
 * @returns 
 */
export async function deleteEmployee(data: EmployeeIdsRequest): Promise<ApiResponse<EmployeeIdsDelResponse>> {
  const stringParams = stringifyParams(data);
  return api.delete<EmployeeIdsDelResponse>(`/employee`, {params: stringParams});
}

/**
 * 임직원 수정
 * @param id 수정할 임직원 ID
 * @param data 수정할 임직원 정보
 * @returns 
 */
export async function updateEmployee(id: string, data: Partial<EmployeeUpdate>): Promise<ApiResponse<EmployeeModResponse>> {
  return api.put<EmployeeModResponse>(`/employee/${id}`, { body: data });
}


/**
 * 임직원 엑셀 등록
 * @param data 
 * @returns 
 */
export async function uploadExcelEmployee(data: EmployeeExcelUploadParameters): Promise<ApiResponse<EmployeeExcelUploadResponse>> {
  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('shopNo', data.shopNo.toString());
  formData.append('type', data.type);
  formData.append('baseField', data.baseField);
  formData.append('groupNo', data.groupNo.toString());
  formData.append('isFixedGroup', String(data.isFixedGroup));
  
  return api.post<EmployeeExcelUploadResponse>('/employee/upload', { formData });
}

/**
 * 엑셀 업로드 task 조회
 * @param id task ID
 * @returns 
 */
export async function getUploadTask(id: string): Promise<ApiResponse<ExcelUpsertTask>> {  
  return api.get<ExcelUpsertTask>(`/task/${id}`);
}

/**
 * 임직원 엑셀 다운로드
 * @param params 
 * @returns 
 */
export async function downloadEmployeeExcel(params: EmployeeListParameters): Promise<Blob> {
  const { type, ...restParams } = params;
  const stringParams = stringifyParams({
    ...restParams,
    type,
    export: 'T' // 엑셀 다운로드 플래그
  });
  const endpoint = type === 'deleted' ? '/employee/deleted' : '/employee';
  return apiDownloadRequest('GET', endpoint, { params: stringParams });
}

/**
 * 임직원 연동회원 수정
 * @param pidId 
 * @param data 
 * @returns 
 */
export async function platformUserIdGroupNoChange(pidId: number, data: GroupNoChangeParameters): Promise<ApiResponse<PlatformUserId>> {
  return api.put<PlatformUserId>(`/employee/pid/${pidId}`, { body: data });
}

export async function employeePlatformUserIdGroupNoChange(pidId: number, data: EmployeeGroupNoChangeParameters): Promise<ApiResponse<EmployeeGroupNoChangeResponse>> {
  return api.put<EmployeeGroupNoChangeResponse>(`/employee/pid/${pidId}/group`, { body: data });
}

export async function employeeListPlatformUserIdGroupNoChange(id: number, data: EmployeeListGroupNoChangeParameters): Promise<ApiResponse<EmployeeListGroupNoChangeResponse>> {
  return api.put<EmployeeListGroupNoChangeResponse>(`/employee/${id}/group`, { body: data });
}

/**
 * 구분 리스트
 * @param params 
 * @returns 
 */
export async function getEmployeeTypeList(params: {shopNo: number}): Promise<ApiResponse<EmployeeTypeList>> {
  const stringParams = stringifyParams(params);
  return api.get<EmployeeTypeList>(`/employee/type`, { params: stringParams });
}

/**
 * 구분 등록
 * @param data 
 * @returns 
 */
export async function createEmployeeType(data: {typeName:string, shopNo: number}): Promise<ApiResponse<EmployeeType>> {
  return api.post<EmployeeType>('/employee/type', { body: data });
}

/**
 * 구분 수정
 * @param id 수정할 구분 ID
 * @param data 수정할 구분명
 * @returns 
 */
export async function updateEmployeeType(typeId: number, data: Partial<{typeName: string}>): Promise<ApiResponse<EmployeeType>> {
  return api.put<EmployeeType>(`/employee/type/${typeId}`, { body: data });
}

/**
 * 임직원 삭제
 * @param id 수정할 구분 ID
 * @param data 수정할 구분
 * @returns 
 */
export async function deleteEmployeeType(typeId: number): Promise<ApiResponse<EmployeeType>> {
  return api.delete<EmployeeType>(`/employee/type/${typeId}`, { body: {} });
}

/**
 * 임직원 일괄 구분 타입 변경
 * @param data 일괄 타입 변경
 * @returns 
 */
export async function updateEmployeeTypeBatch(data: EmployeeTypeBatch) {
  return api.put<EmployeeTypeBatch>(`/employee/type/batch`, { body: data });
}
